import { useDialog } from '@globals/dialog/useDialogStore'
import { colors } from '@globals/themes/defaultTheme'
import { useApiActionToast } from '@globals/toaster/useToasterStore'
import {
  SlackIdentity,
  createSlackIdentity,
  deleteSlackIdentity,
  getSlackIdentity,
} from '@kudos/http-client'
import MoreActionsMenu from '@layouts/standardPageLayout/MoreActionsMenu'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { ConfirmDialog } from '@shared/dialog/ConfirmDialog'
import { ApiError } from '@shared/errors/types'
import { useApiError } from '@shared/errors/useApiError'
import openLinkInNewTab from '@shared/utils/openLinkInNewTab'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { T, useT } from '@transifex/react'

const SLACK_INTEGRATION_REMOVE_DIALOG_KEY = 'slackIntegrationRemoveDialogKey'
const SLACK_INTEGRATION_REINSTALL_DIALOG_KEY = 'slackIntegrationReinstallDialogKey'

const SlackIntegrationCardActions: React.FC = () => {
  const t = useT()
  const queryClient = useQueryClient()
  const [showSuccess] = useApiActionToast()
  const { handleApiError } = useApiError()

  const getSlackIntegrationKey = 'slackIntegration'
  const { data: hasSlackIdentity, isLoading } = useQuery({
    queryKey: [getSlackIntegrationKey],
    queryFn: () => getSlackIdentity(),
  })

  const { openDialog: openRemoveSlackDialog, closeDialog: closeRemoveSlackDialog } = useDialog(
    SLACK_INTEGRATION_REMOVE_DIALOG_KEY,
  )
  const { openDialog: openReinstallSlackDialog, closeDialog: closeReinstallSlackDialog } =
    useDialog(SLACK_INTEGRATION_REINSTALL_DIALOG_KEY)

  const { mutateAsync: installSlack, isPending: isPendingInstall } = useMutation({
    mutationFn: () => createSlackIdentity(),
    onSuccess: (slackIdentity: SlackIdentity) => {
      queryClient.setQueryData([getSlackIntegrationKey], (): boolean => {
        return true
      })
      closeReinstallSlackDialog()
      openLinkInNewTab(slackIdentity.slackUrl)
    },
    onError: error => handleApiError(error as ApiError),
  })

  const { mutateAsync: removeSlackIntegration, isPending: isPendingRemoveSlack } = useMutation({
    mutationFn: () => deleteSlackIdentity(),
    onSuccess: () => {
      queryClient.setQueryData([getSlackIntegrationKey], (): boolean => {
        return false
      })
      showSuccess(t('Slack integration successfully removed'))
      closeRemoveSlackDialog()
    },
    onError: error => handleApiError(error as ApiError),
  })

  if (isLoading) return null

  return (
    <Stack
      direction="row"
      useFlexGap
      sx={{
        alignItems: 'center',
        gap: 1,
      }}>
      {!hasSlackIdentity ? (
        <Button
          sx={{ mt: 0 }}
          id="slackIntegrationBtn"
          variant="outlined"
          disabled={isPendingInstall}
          aria-label={t('Install slack')}
          onClick={() => installSlack()}>
          <T _str="Install" />
          <OpenInNewIcon fontSize="medium" />
        </Button>
      ) : (
        <>
          <Button
            variant="outlined"
            sx={{ mt: 0, color: colors.redPrimary, borderColor: colors.redPrimary }}
            id="removeSlackIntegration"
            aria-label={t('Remove slack integration')}
            disabled={isPendingRemoveSlack}
            onClick={() => openRemoveSlackDialog()}>
            <T _str="Remove" />
          </Button>
          <MoreActionsMenu
            actions={[
              {
                key: 'reinstall',
                variant: 'other',
                disabled: isPendingInstall,
                name: t('Reinstall'),
                onClick: () => openReinstallSlackDialog(),
              },
            ]}
          />
          <ConfirmDialog
            dialogKey={SLACK_INTEGRATION_REMOVE_DIALOG_KEY}
            title={<T _str="Remove Slack integration?" />}
            confirmButtonText={<T _str="Remove" />}
            cancelButtonText={<T _str="Cancel" />}
            onConfirm={removeSlackIntegration}
            variant="danger">
            <span>
              <T _str="This will disconnect Kudos from Slack for all users in your organization. All associated data will be deleted from your workspace. Kudos will no longer be available in Slack." />
            </span>
          </ConfirmDialog>
          <ConfirmDialog
            dialogKey={SLACK_INTEGRATION_REINSTALL_DIALOG_KEY}
            title={<T _str="Reinstall Slack integration?" />}
            confirmButtonText={<T _str="Reinstall" />}
            cancelButtonText={<T _str="Cancel" />}
            onConfirm={installSlack}>
            <span>
              <T _str="This will refresh the Slack connection and help resolve any issues with the integration. Your existing data will be preserved." />
            </span>
          </ConfirmDialog>
        </>
      )}
    </Stack>
  )
}

export default SlackIntegrationCardActions
